import {
  avatar,
  background,
  captions,
  host,
  rule,
  speaker,
  text,
  visualizer,
} from './helpers'
import type { TemplateBlueprint } from './types'

/**
 * `AvatarLayout` (`layouts/AvatarLayout.tsx`): a radial waveform around the
 * speaker, their handle under it, captions beside or below, an episode bar
 * at the foot. One title line and one subtitle line. Intended differences:
 * the handle's line height is 1.2, not v1's `leading-[46px]` (1 at every
 * size): the line clamp hides what spills out of the text box, and at 1 that
 * is the descenders. Its box grows around the same centre. Intended
 * difference (#547): the subtitle (portrait) and both episode lines (square)
 * end at x 810, so a long one is cut clear of the watermark.
 */
const radial = { variant: 'radial' as const }
const handle = { weight: 500, lineHeight: 1.2, opacity: 0.5 }

export const avatarBlueprint: TemplateBlueprint = {
  kind: 'audio',
  orientations: {
    portrait: [
      background(),
      visualizer('portrait', [240, 36, 600, 600], radial),
      avatar('portrait', 390, 186, 300),
      {
        ...speaker(
          'portrait',
          [240, 711, 600, 60],
          'handle',
          { size: 50, ...handle },
          { align: 'center' },
        ),
        id: 'speaker',
      },
      captions('portrait', [80, 780, 920, 900]),
      rule('portrait', 'divider', [0, 1688, 1080, 2], 'border'),
      host('portrait', 50, 1740, 130),
      text('portrait', 'title', [230, 1743, 800, 58], {
        size: 38,
        weight: 600,
        lineHeight: 1.526,
      }),
      text('portrait', 'subtitle', [230, 1809, 580, 58], {
        size: 38,
        weight: 500,
        lineHeight: 1.526,
      }),
    ],
    square: [
      background(),
      visualizer('square', [322, 41, 436, 436], radial),
      avatar('square', 422, 141, 236),
      {
        ...speaker(
          'square',
          [240, 532, 600, 55],
          'handle',
          { size: 46, ...handle },
          { align: 'center' },
        ),
        id: 'speaker',
      },
      captions('square', [80, 600, 920, 300]),
      rule('square', 'divider', [0, 914, 1080, 2], 'border'),
      host('square', 32, 948, 100),
      text('square', 'title', [164, 946, 646, 48], {
        size: 34,
        weight: 600,
        lineHeight: 1.4,
      }),
      text('square', 'subtitle', [164, 1002, 646, 48], {
        size: 34,
        weight: 500,
        lineHeight: 1.4,
      }),
    ],
    landscape: [
      background(),
      visualizer('landscape', [56, 3, 600, 600], radial),
      avatar('landscape', 206, 153, 300),
      {
        ...speaker(
          'landscape',
          [56, 641, 600, 60],
          'handle',
          { size: 50, ...handle },
          { align: 'center' },
        ),
        id: 'speaker',
      },
      captions('landscape', [832, 40, 968, 770]),
      rule('landscape', 'divider', [0, 848, 1920, 2], 'border'),
      host('landscape', 50, 900, 130),
      text('landscape', 'title', [230, 903, 1640, 58], {
        size: 38,
        weight: 600,
        lineHeight: 1.526,
      }),
      text('landscape', 'subtitle', [230, 969, 1640, 58], {
        size: 38,
        weight: 500,
        lineHeight: 1.526,
      }),
    ],
  },
}
